import { createContext, createElement, useContext, type ReactElement, type ReactNode } from 'react';
import type { ChatAPIProxyClient } from '../clients/index.js';

export type CustomPageActionSubject = {
  id: number | string;
  name: string;
  link?: string;
  kind?: string;
};

export enum CustomPageChatContextIdentifier {
  FrmAccountInsights = 'frm-account-insights',
  FrmTerritoryInsights = 'frm-territory-insights',
  FrmPolicySearch = 'frm-policy-search',
  FrmPatientInsights = 'frm-patient-insights',
  FrmFaqs = 'frm-faqs',
}

type NoopCustomPageAction = {
  type: 'noop';
  payload?: object;
};

type UpdateSubjectCustomPageAction = {
  type: 'update-subject';
  payload: {
    subject: CustomPageActionSubject;
  };
};

type OpenNewChatForSubjectCustomPageAction = {
  type: 'open-new-chat-for-subject';
  payload: {
    subject: CustomPageActionSubject;
    chatContextIdentifier: CustomPageChatContextIdentifier;
  };
};

type UpdateInputCustomPageAction = {
  type: 'update-input';
  payload: {
    value: string;
  };
};

type SubmitQueryCustomPageAction = {
  type: 'submit-query';
  payload: {
    query: string;
  };
};

export type CustomPageAction =
  | NoopCustomPageAction
  | UpdateSubjectCustomPageAction
  | OpenNewChatForSubjectCustomPageAction
  | UpdateInputCustomPageAction
  | SubmitQueryCustomPageAction;
export type CustomPageDispatchAction = (action: CustomPageAction) => void;

export type CustomPageSubject = {
  id: number | string;
  name: string;
  kind?: string;
} | null;

export type CustomPageRendererContextValue = {
  proxiesClient: ChatAPIProxyClient | null;
  subject: CustomPageSubject;
  dispatchAction: CustomPageDispatchAction;
};

export const CustomPageProxiesClientContext = createContext<ChatAPIProxyClient | null>(null);
export const CustomPageSubjectContext = createContext<CustomPageSubject>(null);
const noopCustomPageDispatchAction: CustomPageDispatchAction = () => {};
export const CustomPageDispatchActionContext = createContext<CustomPageDispatchAction>(
  noopCustomPageDispatchAction
);

type CustomPageRendererContextProviderProps = {
  value: CustomPageRendererContextValue;
  children: ReactNode;
};

export function CustomPageRendererContextProvider({
  value,
  children,
}: CustomPageRendererContextProviderProps): ReactElement {
  return createElement(
    CustomPageDispatchActionContext.Provider,
    { value: value.dispatchAction },
    createElement(
      CustomPageProxiesClientContext.Provider,
      { value: value.proxiesClient },
      createElement(CustomPageSubjectContext.Provider, { value: value.subject }, children)
    )
  );
}

export function useCustomPageProxiesClient(): ChatAPIProxyClient {
  const ctx = useContext(CustomPageProxiesClientContext);
  if (ctx === null) {
    throw new Error('useCustomPageProxiesClient must be used within a CustomPageRendererContextProvider');
  }
  return ctx;
}

export function useCustomPageSubject(): CustomPageSubject {
  return useContext(CustomPageSubjectContext);
}

export function useCustomPageDispatchAction(): CustomPageDispatchAction {
  return useContext(CustomPageDispatchActionContext);
}
